/* eslint-disable */
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { listOsenOrdersUpdatedSince } from "../api/delivery/osenApi";

const DeliveryCompanySync = () => {
  const orders = useSelector((state) => state.OrdersReducer);
  const store = useSelector((state) => state.StoreReducer);
  const account = useSelector((state) => state.AccountReducer);
  const companySyncRef = useRef({ lastRun: 0, isRunning: false });

  useEffect(() => {
    if (!store || !Array.isArray(orders) || orders.length === 0 || !account?.user) return;

    let isMounted = true;

    const syncCompany = async () => {
      if (!isMounted) return;

      const now = Date.now();
      const syncState = companySyncRef.current;

      // Éviter les synchronisations multiples simultanées
      if (syncState.isRunning) return;
      
      // Vérifier si au moins 1 heure s'est écoulée depuis la dernière sync
      const ONE_HOUR = 60 * 60 * 1000; // 1 heure en millisecondes
      if (now - syncState.lastRun < ONE_HOUR) return;

      syncState.isRunning = true;

      try {
        // ZR EXPRESS
        if (store.Zrexpress && store.ZrexpressC) {
          const zrOrders = orders.filter(
            (item) => item.tracking && item.DC === "2",
          );
          const zrPayload = {
            token: store.Zrexpress,
            key: store.ZrexpressC,
            colis: zrOrders.map((item) => ({ Tracking: item.tracking })),
          };

          try {
            const response = await fetch(
              "https://backend.foorweb.shop/api/lire-zr",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(zrPayload),
              },
            );

            const data = await response.json();
            const updatedColis = data.data.Colis;

            for (const colis of updatedColis) {
              const order = zrOrders.find((o) => o.tracking === colis.Tracking);

              if (order && (order.CompanyDeleveryStatus !== colis.Situation || order.deleveryStatusType !== "Zrexpress")) {
                await setDoc(
                  doc(db, "orders", order.id),
                  {
                    CompanyDeleveryStatus: colis.Situation,
                    deleveryStatusType: "Zrexpress",
                  },
                  { merge: true },
                );
              }
            }
          } catch (error) {
            console.error("❌ Erreur ZR Express :", error);
          }
        }

        // ECOTRACK
        if (store.Uecotrack && store.Ecotrack) {
          const ecoOrders = orders.filter(
            (item) => item.tracking && item.DC === "3",
          );
          const formatDate = (date) => date.toISOString().split("T")[0];
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(endDate.getDate() - 7);

          const startStr = formatDate(startDate);
          const endStr = formatDate(endDate);

          let page = 1;
          let lastPage = 1;
          const allEcoOrders = [];

          try {
            do {
              const response = await fetch(
                `${store.Uecotrack}/api/v1/get/orders?page=${page}&start_date=${startStr}&end_date=${endStr}`,
                {
                  method: "GET",
                  headers: {
                    Authorization: `Bearer ${store.Ecotrack}`,
                    "Content-Type": "application/json",
                  },
                },
              );

              const result = await response.json();
              const commandes = result.data;

              if (!Array.isArray(commandes) || commandes.length === 0) break;

              allEcoOrders.push(...commandes);
              lastPage = result.last_page || 1;
              page++;
            } while (page <= lastPage);

            for (const item of allEcoOrders) {
              const order = ecoOrders.find((o) => o.tracking === item.tracking);

              if (order && (order.CompanyDeleveryStatus !== item.status || order.deleveryStatusType !== "Ecotrack")) {
                await setDoc(
                  doc(db, "orders", order.id),
                  {
                    CompanyDeleveryStatus: item.status,
                    deleveryStatusType: "Ecotrack",
                  },
                  { merge: true },
                );
              }
            }
          } catch (error) {
            console.error("❌ Erreur Ecotrack :", error);
          }
        }

        // ZIMOU
        if (store.zimouToken && store.zimouUrl) {
          const zimouOrders = orders.filter(
            (item) => item.tracking && item.DC === "8",
          );

          try {
            const response = await fetch(
              `${store.zimouUrl}/api2/v2/orders?include=wilaya,commune,products,assigned_users,delivery_type`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  Authorization: `Bearer ${store.zimouToken}`,
                },
              },
            );

            const result = await response.json();
            const commandes = result.data;

            for (const item of commandes) {
              const order = zimouOrders.find(
                (o) => o.tracking === item.tracking_code,
              );

              if (order && (order.CompanyDeleveryStatus !== item.status_name || order.deleveryStatusType !== "Zimou")) {
                await setDoc(
                  doc(db, "orders", order.id),
                  {
                    CompanyDeleveryStatus: item.status_name,
                    deleveryStatusType: "Zimou",
                  },
                  { merge: true },
                );
              }
            }
          } catch (error) {
            console.error("❌ Erreur Zimou :", error);
          }
        }

        // MAYSTRO
        if (store.Maystro) {
          const maystroOrders = orders.filter(
            (item) => item.tracking && item.DC === "1" && item.MaystroId,
          );
          const results = [];

          try {
            for (const order of maystroOrders) {
              const response = await fetch(
                `https://orders-management-dev.maystro-delivery.com/api/stores/history_order/${order.MaystroId}`,
                {
                  method: "GET",
                  headers: {
                    Authorization: `Token ${store.Maystro}`,
                  },
                },
              );

              if (!response.ok) {
                throw new Error(
                  `Erreur pour la commande ${order.id} - HTTP ${response.status}`,
                );
              }

              const history = await response.json();
              results.push({ order, history });
            }

            for (const item of results) {
              const { order, history } = item;

              if (Array.isArray(history) && history.length > 0) {
                const last = history[history.length - 1];
                if (order.CompanyDeleveryStatus !== last.status || order.deleveryStatusType !== "Maystro") {
                  await setDoc(
                    doc(db, "orders", order.id),
                    {
                      CompanyDeleveryStatus: last.status,
                      deleveryStatusType: "Maystro",
                    },
                    { merge: true },
                  );
                }
              }
            }
          } catch (error) {
            console.error("❌ Erreur Maystro :", error);
          }
        }

        // YALIDINE
        if (store.Yalidineid && store.Yalidinetoken) {
          const yalidineOrders = orders.filter(
            (item) => item.tracking && item.DC === "5",
          );

          const trackingCodes = yalidineOrders.map((item) => item.tracking);
          const trackingsParam = trackingCodes.join(",");

          try {
            const response = await fetch(
              `https://backend.foorweb.shop/api/YalidineUpdate?id=${
                store.Yalidineid
              }&token=${store.Yalidinetoken}&trackings=${encodeURIComponent(
                trackingsParam,
              )}`,
            );

            const result = await response.json();
            const parcels = result.data;

            for (const order of yalidineOrders) {
              const match = parcels.find(
                (parcel) => parcel.tracking === order.tracking,
              );

              if (match && (order.CompanyDeleveryStatus !== match.last_status || order.deleveryStatusType !== "Yalidine")) {
                await setDoc(
                  doc(db, "orders", order.id),
                  {
                    CompanyDeleveryStatus: match.last_status,
                    deleveryStatusType: "Yalidine",
                  },
                  { merge: true },
                );
              }
            }
          } catch (error) {
            console.error("❌ Erreur Yalidine :", error);
          }
        }

        // GUEPEX
        if (store.UserguidGuepex && store.Apitokenguepex) {
          const guepexOrders = orders.filter(
            (item) => item.tracking && item.DC === "6",
          );

          const trackingCodes = guepexOrders.map((item) => item.tracking);
          const trackingsParam = trackingCodes.join(",");

          try {
            const response = await fetch(
              `https://backend.foorweb.shop/api/YalidineUpdate?id=${
                store.UserguidGuepex
              }&token=${store.Apitokenguepex}&trackings=${encodeURIComponent(
                trackingsParam,
              )}`,
            );

            const result = await response.json();
            const parcels = result.data;

            for (const order of guepexOrders) {
              const match = parcels.find(
                (parcel) => parcel.tracking === order.tracking,
              );

              if (match && (order.CompanyDeleveryStatus !== match.last_status || order.deleveryStatusType !== "Guepex")) {
                await setDoc(
                  doc(db, "orders", order.id),
                  {
                    CompanyDeleveryStatus: match.last_status,
                    deleveryStatusType: "Guepex",
                  },
                  { merge: true },
                );
              }
            }

          } catch (error) {
            console.error("❌ Erreur Guepex :", error);
          }
        }
        

        if (store.ecomdeliveryv2_key && store.ecomdeliveryv2_token) {
          const ecomDeliveryV2Orders = orders.filter(
            (item) => item.tracking && item.DC === "18",
          );

          const trackingCodes = ecomDeliveryV2Orders.map((item) => item.tracking);
          // GET /colis/statuts accepte au plus 100 trackings par requête.
          const chunks = [];
          for (let i = 0; i < trackingCodes.length; i += 100) {
            chunks.push(trackingCodes.slice(i, i + 100));
          }

          try {
            for (const chunk of chunks) {
              if (chunk.length === 0) continue;

              const response = await fetch(
                "https://ecom-dz.com/api_v2/colis/statuts",
                {
                  method: "POST",
                  headers: {
                    "X-API-Key": store.ecomdeliveryv2_key,
                    "X-API-Token": store.ecomdeliveryv2_token,
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ trackings: chunk }),
                },
              );

              const result = await response.json();
              const items = Array.isArray(result?.trackings)
                ? result.trackings
                : [];

              for (const item of items) {
                if (!item?.trouve) continue;

                const order = ecomDeliveryV2Orders.find(
                  (o) => String(o.tracking) === String(item.tracking),
                );

                if (!order) continue;

                const nextStatus = item.situation;
                const shouldUpdate =
                  String(order.CompanyDeleveryStatus || "") !==
                    String(nextStatus || "") ||
                  order.deleveryStatusType !== "EcomDeliveryV2";

                if (shouldUpdate) {
                  await setDoc(
                    doc(db, "orders", order.id),
                    {
                      CompanyDeleveryStatus: nextStatus,
                      deleveryStatusType: "EcomDeliveryV2",
                      tracking_status: item,
                    },
                    { merge: true },
                  );
                }
              }
            }
          } catch (error) {
            console.error("❌ Erreur Ecom Delivery v2 :", error);
          }
        }

        // OSEN
        if (store.OsenToken) {
          const osenOrders = orders.filter(
            (item) => item.tracking && item.DC === "19",
          );
 
          if (osenOrders.length > 0) {
            try {
              const endDate = new Date();
              const startDate = new Date();
              startDate.setDate(endDate.getDate() - 7);
 
              // Osen has no bulk "trackings[]" lookup, pull everything updated in the window (paginated) and match locally (same approach used for Ecotrack).
              const updated = await listOsenOrdersUpdatedSince(
                store,
                startDate.toISOString(),
                endDate.toISOString(),
              );
 
              for (const item of updated) {
                const order = osenOrders.find(
                  (o) => o.tracking === item.trackingId,
                );
 
                if (
                  order &&
                  (order.CompanyDeleveryStatus !== item.status ||
                    order.deleveryStatusType !== "Osen")
                ) {
                  await setDoc(
                    doc(db, "orders", order.id),
                    {
                      CompanyDeleveryStatus: item.status,
                      deleveryStatusType: "Osen",
                    },
                    { merge: true },
                  );
                }
              }
            } catch (error) {
              console.error("❌ Erreur Osen :", error);
            }
          }
        }

      } catch (error) {
        console.error("❌ Erreur lors de la synchronisation automatique :", error);
      } finally {
        syncState.lastRun = Date.now();
        syncState.isRunning = false;
      }
    };


    // Exécuter immédiatement au démarrage
    syncCompany();
    
    // Puis toutes les heures
    const interval = setInterval(syncCompany, 1 * 60 * 1000); // 1 heure

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [orders, store, account?.user]);

  // Ce composant ne rend rien, il gère seulement la synchronisation en arrière-plan
  return null;
};

export default DeliveryCompanySync;
